import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useEndpoints } from '../../hooks/useEndpoints';
import { useAppSelector } from '../../store';
import { ITestDefinition } from '../../models/testDefinition';
import { ITestResult } from '../../models/testResult';
import { ITeamTestConfig } from '../../models/teamTestConfig';
import { ITestingAnalytics } from '../../models/testingAnalytics';
import { IRTPProtocol, IRTPPhaseStatus } from '../../models/rtpProtocol';

export function useTestingApi(overrideTeamId?: string) {
  const endpoints = useEndpoints();
  const queryClient = useQueryClient();
  const entityId = useAppSelector((st) => st.me.selectedEntity?._id) as string | undefined;
  const teamId = overrideTeamId || entityId;

  // --- Catalogo test ---
  // Catalogo UNIFICATO: pesca dagli esercizi con Cat.Testing (1143), non dalla lista legacy 107.
  const catalogQuery = useQuery({
    queryKey: ['testing-catalog-unified'],
    queryFn: async () => {
      const { data } = await endpoints.getUnifiedCatalog();
      return data;
    },
    staleTime: 1000 * 60 * 30, // 30 min cache - catalog rarely changes
  });

  const catalogByCategoryQuery = (category?: string) => ({
    queryKey: ['testing-catalog-unified', category],
    queryFn: async () => {
      const { data } = await endpoints.getUnifiedCatalog(category ? { category } : undefined);
      return data;
    },
    staleTime: 1000 * 60 * 30,
    enabled: !!category,
  });

  // --- Config team ---
  const teamConfigQuery = useQuery({
    queryKey: ['testing-config', teamId],
    queryFn: async () => {
      const { data } = await endpoints.getTeamTestConfig(teamId!);
      return data;
    },
    enabled: !!teamId,
  });

  const updateTeamConfigMutation = useMutation({
    mutationFn: async (body: Omit<ITeamTestConfig, '_id' | 'teamId'>) => {
      const { data } = await endpoints.updateTeamTestConfig(teamId!, body);
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['testing-config', teamId] });
    },
  });

  // --- Risultati test ---
  const useAthleteResults = (athleteId: string, testId: string, limit?: number) =>
    useQuery({
      queryKey: ['testing-results-athlete', athleteId, testId, limit],
      queryFn: async () => {
        const { data } = await endpoints.getAthleteTestResults(athleteId, { testId, limit });
        return data;
      },
      enabled: !!athleteId && !!testId,
    });

  const useTeamResults = (testId: string, date: string) =>
    useQuery({
      queryKey: ['testing-results-team', teamId, testId, date],
      queryFn: async () => {
        const { data } = await endpoints.getTeamTestResults(teamId!, { testId, date });
        return data;
      },
      enabled: !!teamId && !!testId && !!date,
    });

  const postResultMutation = useMutation({
    mutationFn: async (body: { testId: string; athleteId: string; date: string; value?: number; unit?: string; laterality?: string; notes?: string; exerciseId?: string; protocolId?: string; values?: Record<string, number>; isComplete?: boolean; missingFields?: string[] }) => {
      const { data } = await endpoints.postTestResult({ ...body, teamId: teamId! });
      return data;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['testing-results-athlete', variables.athleteId, variables.testId] });
      queryClient.invalidateQueries({ queryKey: ['testing-results-team', teamId, variables.testId] });
      queryClient.invalidateQueries({ queryKey: ['testing-analytics'] });
    },
  });

  const deleteResultMutation = useMutation({
    mutationFn: async (resultId: string) => {
      await endpoints.deleteTestResult(resultId);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['testing-results-athlete'] });
      queryClient.invalidateQueries({ queryKey: ['testing-results-team'] });
      queryClient.invalidateQueries({ queryKey: ['testing-analytics'] });
    },
  });

  // --- Analytics ---
  const useAnalytics = (athleteId: string, testId: string) =>
    useQuery({
      queryKey: ['testing-analytics', athleteId, teamId, testId],
      queryFn: async () => {
        const { data } = await endpoints.getTestingAnalytics(athleteId, teamId!, testId);
        return data;
      },
      enabled: !!athleteId && !!teamId && !!testId,
    });

  // --- RTP ---
  const useAthleteRTP = (athleteId: string) =>
    useQuery({
      queryKey: ['testing-rtp', athleteId],
      queryFn: async () => {
        const { data } = await endpoints.getAthleteRTP(athleteId);
        return data;
      },
      enabled: !!athleteId,
    });

  const useAthleteRTPCriteria = (athleteId: string) =>
    useQuery({
      queryKey: ['testing-rtp-criteria', athleteId],
      queryFn: async () => {
        const { data } = await endpoints.getAthleteRTPCriteria(athleteId);
        return data;
      },
      enabled: !!athleteId,
    });

  const createRTPMutation = useMutation({
    mutationFn: async (body: { athleteId: string; injuryDate: string; injuryType: string; injurySide: string; enabledTests?: string[]; notes?: string }) => {
      const { data } = await endpoints.postRTPProtocol({ ...body, teamId: teamId! });
      return data;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['testing-rtp', variables.athleteId] });
    },
  });

  const completeRTPMutation = useMutation({
    mutationFn: async ({ protocolId, athleteId }: { protocolId: string; athleteId: string }) => {
      await endpoints.completeRTPProtocol(protocolId);
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['testing-rtp', variables.athleteId] });
    },
  });

  return {
    teamId,
    // Catalog
    catalog: catalogQuery.data ?? [],
    catalogLoading: catalogQuery.isLoading,
    catalogByCategoryQuery,
    // Team config
    teamConfig: teamConfigQuery.data,
    teamConfigLoading: teamConfigQuery.isLoading,
    updateTeamConfig: updateTeamConfigMutation.mutateAsync,
    // Results
    useAthleteResults,
    useTeamResults,
    postResult: postResultMutation.mutateAsync,
    postResultLoading: postResultMutation.isPending,
    deleteResult: deleteResultMutation.mutateAsync,
    // Analytics
    useAnalytics,
    // RTP
    useAthleteRTP,
    useAthleteRTPCriteria,
    createRTP: createRTPMutation.mutateAsync,
    completeRTP: completeRTPMutation.mutateAsync,
  };
}
